import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search,
  Eye,
  Calendar,
  User,
  Loader2
} from 'lucide-react'
import bookingsService, { Booking } from '../services/bookingsService'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { formatBookingSelectedPeriod } from '../utils/courseSchedule'

const Bookings: React.FC = () => {
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [actionStatusFilter, setActionStatusFilter] = useState('ALL')
  const [selectedBooking] = useState<Booking | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [bookingToAction, setBookingToAction] = useState<Booking | null>(null)
  const [processing, setProcessing] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    fetchBookings()
  }, [currentPage, statusFilter, actionStatusFilter, searchTerm])

  const fetchBookings = async () => {
    try {
      setLoading(true)
      const response = await bookingsService.getBookings({
        page: currentPage,
        limit: 10,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        substatus: actionStatusFilter !== 'ALL' ? actionStatusFilter : undefined,
        search: searchTerm.trim() !== '' ? searchTerm : undefined
      })
      
      if (response.success) {
        const bookingsData = response.data.bookings || []
        // Debug: Log booking statuses to verify data
        console.log('Fetched bookings:', bookingsData.map(b => ({ 
          id: b._id, 
          status: b.status, 
          substatus: b.substatus,
          name: b.name,
          shouldShowButtons: b.status.toLowerCase() === 'pending' && b.substatus?.toLowerCase() === 'pending approval from school'
        })))
        setBookings(bookingsData)
        setTotalPages(response.data.pagination?.totalPages || 1)
        setTotalCount(response.data.pagination?.totalCount || 0)
      }
    } catch (error) {
      console.error('Error fetching bookings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApproveClick = (booking: Booking) => {
    setBookingToAction(booking)
    setShowApproveDialog(true)
  }

  const handleRejectClick = (booking: Booking) => {
    setBookingToAction(booking)
    setRejectReason('')
    setShowRejectDialog(true)
  }

  const handleApprove = async () => {
    if (!bookingToAction) return
    try {
      setProcessing(true)
      const response = await bookingsService.approveBooking(bookingToAction._id)
      if (response.success) {
        await fetchBookings()
        setShowModal(false)
        setShowApproveDialog(false)
        setBookingToAction(null)
      }
    } catch (error) {
      console.error('Error approving booking:', error)
      alert('Failed to approve booking. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!bookingToAction) return
    try {
      setProcessing(true)
      const response = await bookingsService.rejectBooking(bookingToAction._id, rejectReason.trim() || undefined)
      if (response.success) {
        await fetchBookings()
        setShowModal(false)
        setShowRejectDialog(false)
        setBookingToAction(null)
        setRejectReason('')
      }
    } catch (error) {
      console.error('Error rejecting booking:', error)
      alert('Failed to reject booking. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string; icon: any }> = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pending', icon: Clock },
      approved: { color: 'bg-primary-100 text-primary-800 border-primary-200', text: 'Approved', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Rejected', icon: XCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800 border-gray-200', text: 'Cancelled', icon: XCircle },
    }
    const config = statusConfig[status.toLowerCase()] || statusConfig.pending
    const Icon = config.icon
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center space-x-1 ${config.color}`}>
        <Icon className="h-3 w-3" />
        <span>{config.text}</span>
      </span>
    )
  }

  const getActionStatusBadge = (substatus?: string) => {
    if (!substatus) return null
    const statusConfig: Record<string, { color: string; text: string }> = {
      'pending approval from school': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending School' },
      'pending payment': { color: 'bg-orange-100 text-orange-800', text: 'Pending Payment' },
    }
    const config = statusConfig[substatus.toLowerCase()]
    if (!config) return null
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.text}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Helper function to check if approve/reject buttons should be shown
  const shouldShowActionButtons = (booking: Booking): boolean => {
    // Only show buttons if:
    // 1. Status is exactly 'pending' (not yet approved/rejected/cancelled)
    // 2. Substatus is exactly 'pending approval from school' (action pending from yoga school side)
    // Do NOT show buttons for approved, rejected, or cancelled bookings
    if (!booking || !booking.status || !booking.substatus) {
      return false
    }
    
    const isPending = booking.status.toLowerCase() === 'pending'
    const isPendingSchool = booking.substatus.toLowerCase() === 'pending approval from school'
    const shouldShow = isPending && isPendingSchool
    
    // Debug: Log if buttons are showing for non-pending bookings
    if (!isPending && (booking.status.toLowerCase() === 'approved' || booking.status.toLowerCase() === 'rejected')) {
      console.warn(`Buttons should NOT show for booking ${booking._id}: status=${booking.status}, substatus=${booking.substatus}`)
    }
    
    return shouldShow
  }

  if (loading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bookings</h1>
          <p className="text-gray-600">Manage course bookings</p>
        </div>
        <div className="text-sm text-gray-500">
          {bookings.length} of {totalCount} bookings
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          {/* Search */}
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search bookings by name, email, or course..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-gray-50"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Substatus Filter */}
          <select
            value={actionStatusFilter}
            onChange={(e) => setActionStatusFilter(e.target.value)}
            className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Substatus</option>
            <option value="pending approval from school">Pending School</option>
            <option value="pending payment">Pending Payment</option>
          </select>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Customer</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Course</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Month</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Submitted</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => (
                <motion.tr
                  key={booking._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{booking.name}</p>
                        <p className="text-sm text-gray-500">{booking.email}</p>
                        {booking.phone && (
                          <p className="text-sm text-gray-600">{booking.phone}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-900">{booking.courseTitle || 'N/A'}</span>
                  </td>
                  <td className="py-4 px-4">
                    {booking.selectedMonth ? (
                      <span className="text-sm text-gray-900">
                        {formatBookingSelectedPeriod(booking.selectedMonth, 'long')}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(booking.status)}
                      {getActionStatusBadge(booking.substatus)}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(booking.submittedAt)}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/bookings/${booking._id}`)}
                        className="p-1 text-gray-600 hover:text-primary-600 transition-colors"
                        title="View Full Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* Only show approve/reject buttons if booking is PENDING and action is pending from school (Y_PENDING) */}
                      {shouldShowActionButtons(booking) ? (
                        <>
                          <button
                            onClick={() => handleApproveClick(booking)}
                            className="p-1 text-gray-600 hover:text-primary-600 transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4 text-primary-600" />
                          </button>
                          <button
                            onClick={() => handleRejectClick(booking)}
                            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {bookings.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No bookings found</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {showModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={() => setShowModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Booking Details
                  </h3>
                  <p className="text-gray-600">{selectedBooking.name} - {selectedBooking.courseTitle || 'Course'}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Customer Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Name</label>
                    <p className="text-gray-900">{selectedBooking.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <p className="text-gray-900">{selectedBooking.email}</p>
                  </div>
                  {selectedBooking.phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Phone</label>
                      <p className="text-gray-900">{selectedBooking.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1 flex items-center flex-wrap gap-1">
                      {getStatusBadge(selectedBooking.status)}
                      {getActionStatusBadge(selectedBooking.substatus)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Booking Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Course</label>
                    <p className="text-gray-900">{selectedBooking.courseTitle || 'N/A'}</p>
                  </div>
                  {selectedBooking.selectedMonth && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Selected Month</label>
                      <p className="text-gray-900">
                        {formatBookingSelectedPeriod(selectedBooking.selectedMonth, 'long')}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Submitted</label>
                    <p className="text-gray-900">
                      {formatDateTime(selectedBooking.submittedAt)}
                    </p>
                  </div>
                  {selectedBooking.approvedAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Approved</label>
                      <p className="text-gray-900">
                        {formatDateTime(selectedBooking.approvedAt)}
                      </p>
                    </div>
                  )}
                  {selectedBooking.rejectedAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Rejected</label>
                      <p className="text-gray-900">
                        {formatDateTime(selectedBooking.rejectedAt)}
                      </p>
                    </div>
                  )}
                  {selectedBooking.paymentCompletedAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Payment Completed</label>
                      <p className="text-gray-900">
                        {formatDateTime(selectedBooking.paymentCompletedAt)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              {selectedBooking.message && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Message</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                    {selectedBooking.message}
                  </p>
                </div>
              )}

              {/* Payment Link */}
              {selectedBooking.paymentLink && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Payment Link</h4>
                  <a 
                    href={selectedBooking.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 text-sm break-all"
                  >
                    {selectedBooking.paymentLink}
                  </a>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              {/* Only show approve/reject buttons if booking is PENDING and action is pending from school (Y_PENDING) */}
              {selectedBooking && shouldShowActionButtons(selectedBooking) ? (
                <>
                  <button
                    onClick={() => handleRejectClick(selectedBooking)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApproveClick(selectedBooking)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Approve
                  </button>
                </>
              ) : null}
            </div>
          </motion.div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showApproveDialog}
        onClose={() => {
          if (!processing) {
            setShowApproveDialog(false)
            setBookingToAction(null)
          }
        }}
        onConfirm={handleApprove}
        title="Approve Booking"
        message={`Are you sure you want to approve the booking for ${bookingToAction?.name || 'this customer'}?`}
        confirmText="Approve"
        cancelText="Cancel"
        confirmButtonColor="green"
        isLoading={processing}
        icon={<CheckCircle className="w-6 h-6 text-primary-600" />}
      />

      {/* Reject Confirmation Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={() => !processing && setShowRejectDialog(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Reject Booking</h3>
                <p className="text-sm text-gray-600">Are you sure you want to reject this booking?</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={3}
                disabled={processing}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  if (!processing) {
                    setShowRejectDialog(false)
                    setBookingToAction(null)
                    setRejectReason('')
                  }
                }}
                disabled={processing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all font-medium text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-sm shadow-lg disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  'Reject'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Bookings
